import {
  type EcrituresRewrite,
  EmailPreference,
  type EmailPreference as EmailPreferenceType,
  EmployeeStatus,
  type EmployeeStatus as EmployeeStatusType,
  EmployeeUrlHash,
  NO_REWRITE,
  type SetTiersAccountInput,
} from '@cpce/shared';
import { ArrowUpRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSetJustificatifValidationStatus } from '@/api/beneficiaries/beneficiaries.mutations';
import {
  useSetEmployeeFinancialAccount,
  useUpdateEmployee,
} from '@/api/employees/employees.mutations';
import { useEmployeeFinancialAccount, useSites } from '@/api/employees/employees.queries';
import type {
  EmployeeBeneficiaryFormatted,
  EmployeeWithBeneficiariesFormatted,
  UpdateEmployeeData,
} from '@/api/employees/employees.types';
import { Button } from '@/components/Button';
import { CardEditActions } from '@/components/CardEditActions';
import { CardWithTitle } from '@/components/CardWithTitle';
import { Combobox } from '@/components/Combobox';
import { DatePicker } from '@/components/DatePicker';
import { FieldsGrid } from '@/components/FieldsGrid';
import { Input } from '@/components/Input';
import { ScrollToUrlHash } from '@/components/ScrollToUrlHash';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/Select';
import { useTiersAccountChoice } from '@/components/useTiersAccountChoice';
import { formatDate } from '@/utils/date.utils';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { BeneficiaryDocumentsCard } from '../../components/BeneficiaryDocumentsCard';
import { EmployeeBankDetailsCard } from './EmployeeBankDetailsCard';

type EditingCard = 'entreprise' | 'comptabilite' | null;

interface FormState {
  professionalEmail: string;
  service: string;
  status: EmployeeStatusType;
  siteIri: string;
  lastContractDate: string;
  contractStartDate: string;
  contractEndDate: string;
  matricule: string;
  indiceDeReference: string;
}

function formatDateForApi(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

interface EmployeeInformationProps {
  employee: EmployeeWithBeneficiariesFormatted;
}

export function EmployeeInformation(props: EmployeeInformationProps) {
  const { employee } = props;
  const navigate = useCommiteeSlugNavigate();
  const { data: sites, isLoading: isLoadingSites } = useSites();
  const { mutate: updateEmployee, isPending: isUpdatingEmployee } = useUpdateEmployee(employee.id);
  const { mutate: setEmployeeFinancialAccount, isPending: isSettingAccount } =
    useSetEmployeeFinancialAccount(employee.id);
  const { data: accountState } = useEmployeeFinancialAccount(employee.id);
  const isSaving = isUpdatingEmployee || isSettingAccount;
  const beneficiary = employee.beneficiary;
  const spouse: EmployeeBeneficiaryFormatted | undefined = useMemo(
    () => employee.employeeBeneficiaries.find((eb) => eb.status === 'married'),
    [employee.employeeBeneficiaries]
  );
  const children: EmployeeBeneficiaryFormatted[] = useMemo(
    () => employee.employeeBeneficiaries.filter((eb) => eb.status === 'child'),
    [employee.employeeBeneficiaries]
  );
  const { mutate: setJustificatifValidationStatus } = useSetJustificatifValidationStatus();

  const buildFormState = (): FormState => ({
    professionalEmail: employee.professionalEmailAddress ?? '',
    service: employee.service ?? '',
    status: employee.status ?? EmployeeStatus.salaried,
    siteIri: employee.site?.iri ?? '',
    lastContractDate: formatDateForApi(employee.lastContractDate) ?? '',
    contractStartDate: formatDateForApi(employee.contractStartDate) ?? '',
    contractEndDate: formatDateForApi(employee.contractEndDate) ?? '',
    matricule: employee.matricule ?? '',
    indiceDeReference: employee.subventionCoefficient
      ? Number(employee.subventionCoefficient).toString()
      : '',
  });

  const [editingCard, setEditingCard] = useState<EditingCard>(null);
  const [formState, setFormState] = useState<FormState>(buildFormState);
  const isEditingEntreprise = editingCard === 'entreprise';
  const isEditingComptabilite = editingCard === 'comptabilite';

  // Only the fields the user changed go to the billetterie card, and the card closes once every
  // request has succeeded, so a failed 411 creation leaves the form open with its input.
  const save = async (accountInput: SetTiersAccountInput | null): Promise<EcrituresRewrite> => {
    const initial = buildFormState();
    const changed = (key: keyof FormState) => formState[key] !== initial[key];
    const cardFields: UpdateEmployeeData['cardFields'] = {};

    if (editingCard === 'entreprise') {
      if (changed('professionalEmail'))
        cardFields.emailProfessionnel = formState.professionalEmail || null;
      if (changed('service')) cardFields.service = formState.service || null;
      // The billetterie keeps retirees in statutProfessionnel; salarieStatut only knows salarie/stagiaire.
      if (changed('status')) {
        if (formState.status === EmployeeStatus.retired) {
          cardFields.statutProfessionnel = 'Retraité';
        } else {
          cardFields.salarieStatut = formState.status;
          if (initial.status === EmployeeStatus.retired) cardFields.statutProfessionnel = null;
        }
      }
      if (changed('siteIri')) cardFields.site = formState.siteIri || null;
      if (changed('lastContractDate'))
        cardFields.dateDernierContrat = formState.lastContractDate || null;
      if (changed('contractStartDate')) cardFields.dateEntree = formState.contractStartDate || null;
      if (changed('contractEndDate')) cardFields.dateSortie = formState.contractEndDate || null;
      if (changed('matricule')) cardFields.matricule = formState.matricule || null;
    } else if (editingCard === 'comptabilite' && changed('indiceDeReference')) {
      cardFields.indiceDeReference = formState.indiceDeReference || null;
    }

    const [cardUpdate, accountUpdate] = await Promise.allSettled([
      Object.keys(cardFields).length > 0 ? updateEmployee({ cardFields }) : null,
      accountInput ? setEmployeeFinancialAccount(accountInput) : NO_REWRITE,
    ]);
    if (cardUpdate.status === 'rejected') throw cardUpdate.reason;
    if (accountUpdate.status === 'rejected') throw accountUpdate.reason;
    setEditingCard(null);
    return accountUpdate.value;
  };

  const accountChoice = useTiersAccountChoice({
    tiers: 'salarié',
    account: employee.financialAccountCode
      ? { code: employee.financialAccountCode, name: employee.financialAccountName ?? null }
      : null,
    state: accountState,
    defaultName: [beneficiary.firstName, beneficiary.lastName].filter(Boolean).join(' '),
    isEditing: isEditingComptabilite,
    canKeep: true,
    save,
  });

  const handleStartEditing = (card: EditingCard) => {
    setFormState(buildFormState());
    accountChoice.reset();
    setEditingCard(card);
  };

  return (
    <div className="flex flex-col gap-4">
      <CardWithTitle title="Coordonnées">
        <FieldsGrid
          fields={[
            {
              title: 'Nom',
              value: beneficiary.lastName,
            },
            {
              title: 'Prénom',
              value: beneficiary.firstName,
            },
            {
              title: 'Genre',
              value: beneficiary.genderFormatted,
            },
            {
              title: 'Date de naissance',
              value: formatDate(beneficiary.birthDate),
            },
            {
              title: 'Mail personnel',
              value: employee.personalEmailAddress,
            },
            {
              title: 'Mail préféré',
              value: (
                <Select
                  value={
                    employee.emailPreference ??
                    (employee.personalEmailAddress
                      ? EmailPreference.personal
                      : EmailPreference.professional)
                  }
                  onValueChange={(val) =>
                    updateEmployee({
                      cardFields: { preferenceEmail: val as EmailPreferenceType },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EmailPreference.personal}>Personnel</SelectItem>
                    <SelectItem value={EmailPreference.professional}>Professionnel</SelectItem>
                  </SelectContent>
                </Select>
              ),
            },
            {
              title: 'Adresse',
              value: employee.street,
            },
            {
              title: 'Code postal',
              value: employee.postalCode,
            },
            {
              title: 'Ville',
              value: employee.city,
            },
          ]}
        />
      </CardWithTitle>
      <CardWithTitle
        title="Entreprise"
        actions={
          <CardEditActions
            isEditing={isEditingEntreprise}
            onSave={accountChoice.requestSave}
            isPending={isSaving}
            onEdit={() => handleStartEditing('entreprise')}
          />
        }
      >
        <FieldsGrid
          fields={[
            {
              title: 'Mail professionnel',
              value: isEditingEntreprise ? (
                <Input
                  value={formState.professionalEmail}
                  onChange={(e) =>
                    setFormState({ ...formState, professionalEmail: e.target.value })
                  }
                  placeholder="Mail professionnel"
                />
              ) : (
                employee.professionalEmailAddress
              ),
            },
            {
              title: 'Service',
              value: isEditingEntreprise ? (
                <Input
                  value={formState.service}
                  onChange={(e) => setFormState({ ...formState, service: e.target.value })}
                  placeholder="Service"
                />
              ) : (
                employee.service
              ),
            },
            {
              title: 'Statut',
              value: isEditingEntreprise ? (
                <Select
                  value={formState.status}
                  onValueChange={(val) =>
                    setFormState({ ...formState, status: val as EmployeeStatusType })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EmployeeStatus.salaried}>Salarié</SelectItem>
                    <SelectItem value={EmployeeStatus.internship}>Stagiaire</SelectItem>
                    <SelectItem value={EmployeeStatus.retired}>Retraité</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                employee.statusFormatted
              ),
            },
            {
              title: 'Site',
              value: isEditingEntreprise ? (
                <Combobox
                  options={[
                    { value: '', label: 'Aucun' },
                    ...(sites?.map((s) => ({ value: s['@id'], label: s.name })) ?? []),
                  ]}
                  value={formState.siteIri}
                  onChange={(val) => setFormState({ ...formState, siteIri: val })}
                  isLoading={isLoadingSites}
                />
              ) : (
                employee.site?.name
              ),
            },
            {
              title: 'Date de dernier contrat',
              value: isEditingEntreprise ? (
                <DatePicker
                  value={
                    formState.lastContractDate ? new Date(formState.lastContractDate) : undefined
                  }
                  onChange={(date) =>
                    setFormState({
                      ...formState,
                      lastContractDate: formatDateForApi(date ?? null) ?? '',
                    })
                  }
                />
              ) : (
                formatDate(employee.lastContractDate)
              ),
            },
            {
              title: "Date d'entrée",
              value: isEditingEntreprise ? (
                <DatePicker
                  value={
                    formState.contractStartDate ? new Date(formState.contractStartDate) : undefined
                  }
                  onChange={(date) =>
                    setFormState({
                      ...formState,
                      contractStartDate: formatDateForApi(date ?? null) ?? '',
                    })
                  }
                />
              ) : (
                formatDate(employee.contractStartDate)
              ),
            },
            {
              title: 'Date de sortie',
              value: isEditingEntreprise ? (
                <DatePicker
                  value={
                    formState.contractEndDate ? new Date(formState.contractEndDate) : undefined
                  }
                  onChange={(date) =>
                    setFormState({
                      ...formState,
                      contractEndDate: formatDateForApi(date ?? null) ?? '',
                    })
                  }
                />
              ) : (
                formatDate(employee.contractEndDate)
              ),
            },
            {
              title: 'Date de désactivation',
              value: formatDate(employee.deactivationDate),
            },
            {
              title: 'Subventions désactivées le',
              value: formatDate(employee.subsidiesDisabledAt),
            },
            {
              title: 'Matricule',
              value: isEditingEntreprise ? (
                <Input
                  value={formState.matricule}
                  onChange={(e) => setFormState({ ...formState, matricule: e.target.value })}
                  placeholder="Matricule"
                />
              ) : (
                employee.matricule
              ),
            },
            {
              title: 'Ancienneté',
              value: employee.seniority,
            },
            {
              title: 'Téléphone',
              value: employee.professionalPhoneNumber,
            },
          ]}
        />
      </CardWithTitle>
      <EmployeeBankDetailsCard employeeId={employee.id} iban={employee.iban} bic={employee.bic} />
      <CardWithTitle title="Situation familiale">
        <FieldsGrid
          fields={[
            {
              title: 'Statut marital',
              value: spouse ? 'Marié(e)' : 'Non marié(e)',
            },
            {
              // The conjoint is an ayant-droit, edited from the "Ayants-droit" tab.
              title: 'Conjoint',
              value: spouse ? (
                <Button
                  className="text-primary"
                  icon={ArrowUpRight}
                  onClick={() => {
                    navigate({
                      to: '/$commiteeSlug/beneficiaries/employees-beneficiaries/{-$id}',
                      params: { id: spouse.beneficiary.id.toString() },
                    });
                  }}
                >
                  {spouse.beneficiary.fullName}
                </Button>
              ) : null,
            },
            {
              title: "Nombre d'enfants",
              value: children.length.toString(),
            },
            {
              title: "Nombre d'enfants à charge",
              value: children.filter((child) => child.beneficiary.aCharge).length.toString(),
            },
            {
              title: "Nombre d'ayants-droits",
              value: employee.employeeBeneficiaries.length.toString(),
            },
          ]}
        />
      </CardWithTitle>
      <CardWithTitle
        title="Coefficient et comptabilité"
        actions={
          <CardEditActions
            isEditing={isEditingComptabilite}
            onSave={accountChoice.requestSave}
            isPending={isSaving}
            onEdit={() => handleStartEditing('comptabilite')}
          />
        }
      >
        <FieldsGrid
          fields={[
            {
              title: 'Coefficient de subvention',
              value: isEditingComptabilite ? (
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={formState.indiceDeReference}
                  onChange={(e) =>
                    setFormState({ ...formState, indiceDeReference: e.target.value })
                  }
                  placeholder="Coefficient"
                />
              ) : employee.subventionCoefficient ? (
                Number(employee.subventionCoefficient).toString()
              ) : undefined,
            },
            {
              title: 'Compte client',
              value: isEditingComptabilite ? accountChoice.editor : accountChoice.view,
            },
          ]}
        />
      </CardWithTitle>
      {accountChoice.modal}
      <ScrollToUrlHash urlHash={EmployeeUrlHash.DocumentAlert}>
        <BeneficiaryDocumentsCard
          title={`Justificatifs du salarié (${beneficiary.externalJustificatifs?.length ?? 0})`}
          beneficiary={beneficiary}
          justificatifs={beneficiary.externalJustificatifs ?? []}
          onDocumentStatusChange={(justificatif, validated) => {
            setJustificatifValidationStatus({
              justificatifId: justificatif.id,
              validated,
              externalEmployeeId: employee.id,
              documentName: justificatif.type,
            });
          }}
        />
      </ScrollToUrlHash>
    </div>
  );
}
